'use client';

import { useCallback, useState, type FormEvent } from 'react';
import type { PasswordFormProps } from './types';

/**
 * Password Form Component
 * 
 * Secure password verification form for bypassing the Coming Soon page.
 * Uses server-side verification with bcrypt comparison.
 */
export function PasswordForm({
  organisationId,
  onSuccess,
  locale: _locale,
  messages: _messages,
}: PasswordFormProps) {
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      setError(null);
      setIsLoading(true);

      try {
        const response = await fetch('/api/coming-soon/verify', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            organisationId,
            password,
          }),
        });

        const data = await response.json();

        if (response.ok && data.success) {
          // Password verified - trigger success callback
          onSuccess();
        } else {
          setError(data.error || 'Incorrect password. Please try again.');
        }
      } catch {
        setError('An error occurred. Please try again.');
      } finally {
        setIsLoading(false);
      }
    },
    [organisationId, password, onSuccess]
  );

  return (
    <form onSubmit={handleSubmit} className="w-full max-w-sm mx-auto">
      <div className="space-y-4">
        <div>
          <label htmlFor="password" className="sr-only">
            Access Password
          </label>
          <input
            type="password"
            id="password"
            name="password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Enter access password"
            required
            autoComplete="current-password"
            className="w-full px-4 py-3 rounded-lg border border-gray-300 bg-white/90 backdrop-blur-sm text-gray-900 placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
            disabled={isLoading}
          />
        </div>

        {error && (
          <div
            className="text-red-600 text-sm text-center bg-red-50 px-4 py-2 rounded-lg"
            role="alert"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={isLoading || !password}
          className="w-full px-6 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-all"
        >
          {isLoading ? (
            <span className="flex items-center justify-center gap-2">
              <svg
                className="animate-spin h-5 w-5"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
              >
                <circle
                  className="opacity-25"
                  cx="12"
                  cy="12"
                  r="10"
                  stroke="currentColor"
                  strokeWidth="4"
                />
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                />
              </svg>
              Verifying...
            </span>
          ) : (
            'Access Site'
          )}
        </button>
      </div>
    </form>
  );
}
